import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';
import api from '../lib/api';

/**
 * Global currency context.
 *
 * Resolution order at first mount:
 *   1. localStorage.talentiq_currency   (previous choice on this browser)
 *   2. authenticated user's `currency_preference` (persisted server-side)
 *   3. GET /api/pricing/detect         (best-effort geo)
 *   4. DEFAULT_CURRENCY = "eur"
 *
 * Any change → localStorage + (if user logged in) PATCH /api/users/me/preferences.
 */

const CurrencyContext = createContext({
    currency: 'eur',
    country: 'FR',
    currencies: [],
    currencyMeta: { symbol: '€', locale: 'fr-FR', decimals: 2 },
    setCurrency: () => {},
    isReady: false,
});

const STORAGE_KEY = 'talentiq_currency';
const COUNTRY_STORAGE_KEY = 'talentiq_country';

export function CurrencyProvider({ user, children }) {
    const [currency, setCurrencyState] = useState('eur');
    const [country, setCountry] = useState('FR');
    const [currencies, setCurrencies] = useState([]);
    const [isReady, setIsReady] = useState(false);

    // Bootstrap
    useEffect(() => {
        let cancelled = false;

        const boot = async () => {
            // 1. Load the catalog of supported currencies
            try {
                const res = await api.get('/pricing/currencies');
                if (!cancelled) setCurrencies(res.data?.currencies || []);
            } catch { /* silent */ }

            // 2. Resolve initial currency
            const stored = typeof window !== 'undefined' ? localStorage.getItem(STORAGE_KEY) : null;
            const storedCountry = typeof window !== 'undefined' ? localStorage.getItem(COUNTRY_STORAGE_KEY) : null;
            let chosen = stored;
            let chosenCountry = storedCountry;

            if (!chosen && user?.currency_preference) {
                chosen = user.currency_preference;
                chosenCountry = user.country_preference || chosenCountry;
            }

            if (!chosen) {
                try {
                    const res = await api.get('/pricing/detect');
                    if (res.data?.currency) {
                        chosen = res.data.currency;
                        chosenCountry = res.data.country;
                    }
                } catch { /* silent */ }
            }

            if (!cancelled) {
                setCurrencyState((chosen || 'eur').toLowerCase());
                if (chosenCountry) setCountry(chosenCountry.toUpperCase());
                setIsReady(true);
            }
        };

        boot();
        return () => { cancelled = true; };
    }, [user?.currency_preference, user?.country_preference]);

    // Change handler — persists everywhere it matters
    const setCurrency = useCallback(async (newCurrency, newCountry = null) => {
        const code = (newCurrency || 'eur').toLowerCase();
        setCurrencyState(code);
        if (newCountry) setCountry(newCountry.toUpperCase());
        if (typeof window !== 'undefined') {
            localStorage.setItem(STORAGE_KEY, code);
            if (newCountry) localStorage.setItem(COUNTRY_STORAGE_KEY, newCountry.toUpperCase());
        }
        // Persist for authenticated users (fire-and-forget)
        if (user?.id) {
            try {
                await api.patch('/users/me/preferences', {
                    currency_preference: code,
                    ...(newCountry ? { country_preference: newCountry.toUpperCase() } : {}),
                });
            } catch { /* silent */ }
        }
    }, [user?.id]);

    const currencyMeta = useMemo(() => {
        return currencies.find(c => c.code === currency) || { symbol: currency.toUpperCase(), locale: 'fr-FR', decimals: 2 };
    }, [currencies, currency]);

    const value = useMemo(() => ({
        currency,
        country,
        currencies,
        currencyMeta,
        setCurrency,
        isReady,
    }), [currency, country, currencies, currencyMeta, setCurrency, isReady]);

    return <CurrencyContext.Provider value={value}>{children}</CurrencyContext.Provider>;
}

export function useCurrency() {
    return useContext(CurrencyContext);
}
